import { AnimatedPageHeader } from '@components/index'
import { Table } from '@components/table'
import { PageRead } from '@components/table/types'
import { useFlats } from '@services/flats/queries/useFlats'
import { CircleCheck, CircleX, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { FlatList } from 'types/api'

import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

import { FlatsTableFilters } from './FlatsTableFilters'

const STATUS_TONE: Record<string, string> = {
  AVAILABLE: 'bg-success-soft text-success',
  RENTED: 'bg-info-soft text-info',
  DRAFT: 'bg-peach/25 text-warning',
  NOT_AVAILABLE: 'bg-ink/[0.07] text-ink/60'
}

type PersonRef = {
  id: string
  first_name?: string | null
  last_name?: string | null
  email?: string | null
}

function PeopleLinks({
  people,
  base
}: {
  people: unknown
  base: string
}) {
  if (!Array.isArray(people) || people.length === 0)
    return <span className="text-ink/40">—</span>
  return (
    <div className="flex flex-wrap gap-x-1">
      {(people as PersonRef[]).map((p, index) => (
        <span key={p.id}>
          <Link
            to={`${base}/${p.id}`}
            onClick={(e) => e.stopPropagation()}
            className="text-primary transition-colors hover:underline"
          >
            {`${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email}
          </Link>
          {index < people.length - 1 ? ', ' : ''}
        </span>
      ))}
    </div>
  )
}

function DepositHeader() {
  const { t } = useTranslation('flat')
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help items-center gap-1.5">
            {t('table.deposit')}
            <Info className="h-3.5 w-3.5 text-ink/35" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[320px] space-y-2">
          <p>{t('table.deposit_tooltip_ratio')}</p>
          <p>{t('table.deposit_tooltip_direct')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function DepositCell({
  flatDeposit,
  record
}: {
  flatDeposit: number | null
  record: FlatList
}) {
  const { t } = useTranslation('flat')

  // Settled outside SNA: nothing is ever collected from the client, so the
  // paid/owed pair would only ever read 0 and mislead.
  if (record.deposit_paid_directly) {
    return (
      <span className="inline-flex items-center whitespace-nowrap rounded-full bg-ink/[0.07] px-2.5 py-1 text-xs font-semibold text-ink/60">
        {t('table.deposit_direct')}
      </span>
    )
  }

  // No reserved or ongoing offer - there is no client to have paid anything.
  if (record.deposit_paid == null) {
    return <span className="text-ink/40">—</span>
  }

  return (
    <span className="whitespace-nowrap">
      <span className="font-semibold text-ink">{record.deposit_paid}</span>
      <span className="text-muted-foreground">
        {' / '}
        {flatDeposit ?? 0} €
      </span>
    </span>
  )
}

type FlatsTableProps = {
  landlordId?: string
}

export function FlatsTable({ landlordId }: FlatsTableProps = {}) {
  const query = useFlats({
    params: landlordId ? { landlord_id: landlordId } : undefined
  })
  const { t } = useTranslation('flat')
  const navigate = useNavigate()

  const columns = [
    {
      title: t('table.title'),
      dataIndex: 'title',
      sorter: true,
      render: (value: string) => (
        <span className="font-medium text-ink">{value || '—'}</span>
      )
    },
    {
      title: t('table.landlord'),
      dataIndex: 'landlords',
      sorter: true,
      render: (landlords: unknown) => (
        <PeopleLinks people={landlords} base="/admin/landlords" />
      )
    },
    {
      title: t('table.clients'),
      dataIndex: 'clients',
      render: (clients: unknown) => (
        <PeopleLinks people={clients} base="/admin/users" />
      )
    },
    {
      title: t('table.rent'),
      dataIndex: 'monthly_rent_sum',
      sorter: false,
      render: (value: number | null) =>
        value == null ? (
          <span className="text-ink/40">—</span>
        ) : (
          <span className="whitespace-nowrap font-semibold text-ink">
            {value} <span className="font-normal text-muted-foreground">€</span>
          </span>
        )
    },
    {
      title: <DepositHeader />,
      key: 'deposit',
      dataIndex: 'deposit',
      render: (flatDeposit: number | null, record: FlatList) => (
        <DepositCell flatDeposit={flatDeposit} record={record} />
      )
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      sorter: true,
      render: (value: string) => (
        <span
          className={cn(
            'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
            STATUS_TONE[value] ?? 'bg-ink/[0.07] text-ink/60'
          )}
        >
          {t(`statuses.${value}`)}
        </span>
      )
    },
    {
      title: t('table.onboarded'),
      dataIndex: 'onboarded',
      sorter: true,
      render: (value: boolean) => (
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
            value ? 'bg-primary/15 text-primary' : 'bg-ink/[0.07] text-ink/60'
          )}
        >
          {value ? (
            <CircleCheck className="h-3.5 w-3.5" />
          ) : (
            <CircleX className="h-3.5 w-3.5" />
          )}
          {value ? t('table.yes') : t('table.no')}
        </span>
      )
    }
  ]

  return (
    <div className="space-y-6">
      {!landlordId && (
        <>
          <AnimatedPageHeader title={t('title')} />
          <FlatsTableFilters
            actionBar={
              <Button onClick={() => navigate('/admin/flats/create')}>
                {t('filters.createFlat')}
              </Button>
            }
          />
        </>
      )}
      <Table<PageRead<FlatList>>
        query={query}
        columns={columns}
        id="flats"
        hasSearch={false}
      />
    </div>
  )
}
